import { currentTheme } from "../lib/theme";

const SHARED_THEME = {
  accentColor: "orange",
  grayColor: "slate",
  dangerColor: "red",
  warningColor: "orange",
  successColor: "orange",
  infoColor: "sky",
};

const SHARED_VARIABLES = {
  "--radius": "10px",
  "--default-font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "--heading-font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "--strong-font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "--default-letter-spacing": "-0.01em",
  "--heading-letter-spacing": "-0.035em",
  "--table-row-background-color": "transparent",
};

const SHARED_CLASSES = {
  "whop-FieldLabel": { fontSize: "15px", fontWeight: "650" },
  "whop-FieldDescription": { fontSize: "13px", lineHeight: "1.5" },
  "whop-InputRoot": { minHeight: "48px", borderRadius: "10px" },
  "whop-Input": { padding: "12px 14px", fontSize: "15px" },
  "whop-SelectTrigger": { minHeight: "48px", padding: "10px 14px", borderRadius: "10px", fontSize: "15px" },
  "whop-SelectPopup": { borderRadius: "10px" },
  "whop-DialogContent": { borderRadius: "18px" },
  "whop-DialogHeader": { padding: "20px 22px" },
  "whop-DialogBody": { padding: "20px 22px" },
  "whop-DialogFooter": { padding: "16px 22px 20px" },
};

export const PORTAL_ELEMENTS_APPEARANCE_DARK = {
  theme: { appearance: "dark", ...SHARED_THEME },
  variables: {
    ...SHARED_VARIABLES,
    "--color-background": "#0B0E14",
    "--color-panel-solid": "#12161F",
    "--color-panel-translucent": "#12161F",
    "--color-stroke": "rgba(255, 255, 255, 0.10)",
    "--accent-1": "#1A120C",
    "--accent-2": "#21150C",
    "--accent-3": "#2A1B10",
    "--accent-4": "#352010",
    "--accent-5": "#43270F",
    "--accent-6": "#543013",
    "--accent-7": "#6B3A15",
    "--accent-8": "#8F4A17",
    "--accent-9": "#FF6B2C",
    "--accent-10": "#E5561B",
    "--accent-11": "#FF8B55",
    "--accent-12": "#FFD3BA",
    "--accent-9-contrast": "#140A05",
    "--gray-1": "#12161F",
    "--gray-2": "#151A24",
    "--gray-3": "#1A2029",
    "--gray-4": "#1E2532",
    "--gray-5": "#232A3A",
    "--gray-6": "#2A3143",
    "--gray-7": "#333B50",
    "--gray-8": "#444E66",
    "--gray-9": "#6B7593",
    "--gray-10": "#7A84A0",
    "--gray-11": "#9AA1B4",
    "--gray-12": "#EDEFF4",
    "--orange-9": "#f76b15",
    "--orange-10": "#ef5f00",
    "--orange-11": "#FF8B55",
    "--orange-12": "#FFD3BA",
    "--amber-1": "#12161F",
    "--amber-2": "#151A24",
    "--amber-3": "#1A2029",
    "--amber-4": "#1E2532",
    "--amber-5": "#232A3A",
    "--amber-6": "#2A3143",
    "--amber-7": "#333B50",
    "--amber-8": "#444E66",
    "--amber-9": "#ffc53d",
    "--amber-10": "#ffba18",
    "--amber-11": "#E8B54A",
    "--amber-12": "#F5DFAE",
    "--amber-a2": "rgba(255, 255, 255, 0.03)",
    "--amber-surface": "rgba(255, 197, 61, 0.08)",
    "--amber-9-contrast": "#140A05",
    "--yellow-1": "#12161F",
    "--yellow-2": "#151A24",
    "--yellow-3": "#1A2029",
    "--yellow-4": "#1E2532",
    "--yellow-5": "#232A3A",
    "--yellow-6": "#2A3143",
    "--yellow-7": "#333B50",
    "--yellow-8": "#444E66",
    "--yellow-9": "#ffc53d",
    "--yellow-10": "#ffba18",
    "--yellow-11": "#E8B54A",
    "--yellow-12": "#F5DFAE",
    "--yellow-a2": "rgba(255, 255, 255, 0.03)",
    "--yellow-surface": "rgba(255, 197, 61, 0.08)",
    "--yellow-9-contrast": "#140A05",
  },
  classes: SHARED_CLASSES,
};

export const PORTAL_ELEMENTS_APPEARANCE_LIGHT = {
  theme: { appearance: "light", ...SHARED_THEME },
  variables: {
    ...SHARED_VARIABLES,
    "--color-background": "#F4F6FA",
    "--color-panel-solid": "#ffffff",
    "--color-panel-translucent": "#ffffff",
    "--color-stroke": "rgba(24, 24, 24, 0.10)",
    "--accent-1": "#fdfdfe",
    "--accent-2": "#fff7f2",
    "--accent-3": "#ffefe5",
    "--accent-4": "#ffe5d5",
    "--accent-5": "#ffd9c2",
    "--accent-6": "#ffc9a8",
    "--accent-7": "#ffb587",
    "--accent-8": "#ff9c5f",
    "--accent-9": "#FF6B2C",
    "--accent-10": "#E5561B",
    "--accent-11": "#C24A12",
    "--accent-12": "#582d1d",
    "--accent-9-contrast": "#ffffff",
    "--gray-1": "#ffffff",
    "--gray-2": "#fafbfc",
    "--gray-3": "#f2f3f6",
    "--gray-4": "#e9ebee",
    "--gray-5": "#e1e3e8",
    "--gray-6": "#d7dade",
    "--gray-7": "#c8ccd2",
    "--gray-8": "#a8adb8",
    "--gray-9": "#7b8090",
    "--gray-10": "#6d7280",
    "--gray-11": "#565b66",
    "--gray-12": "#181818",
    "--orange-9": "#f76b15",
    "--orange-10": "#ef5f00",
    "--orange-11": "#cc4e00",
    "--orange-12": "#582d1d",
    "--amber-1": "#fdfdfe",
    "--amber-2": "#fafbfc",
    "--amber-3": "#f2f3f6",
    "--amber-4": "#e9ebee",
    "--amber-5": "#e1e3e8",
    "--amber-6": "#d7dade",
    "--amber-7": "#c8ccd2",
    "--amber-8": "#a8adb8",
    "--amber-9": "#ffc53d",
    "--amber-10": "#ffba18",
    "--amber-11": "#ab6400",
    "--amber-12": "#4f3422",
    "--amber-a2": "rgba(24, 24, 24, 0.025)",
    "--amber-surface": "rgba(255, 197, 61, 0.08)",
    "--amber-9-contrast": "#181818",
    "--yellow-1": "#fdfdfe",
    "--yellow-2": "#fafbfc",
    "--yellow-3": "#f2f3f6",
    "--yellow-4": "#e9ebee",
    "--yellow-5": "#e1e3e8",
    "--yellow-6": "#d7dade",
    "--yellow-7": "#c8ccd2",
    "--yellow-8": "#a8adb8",
    "--yellow-9": "#ffc53d",
    "--yellow-10": "#ffba18",
    "--yellow-11": "#ab6400",
    "--yellow-12": "#4f3422",
    "--yellow-a2": "rgba(24, 24, 24, 0.025)",
    "--yellow-surface": "rgba(255, 197, 61, 0.08)",
    "--yellow-9-contrast": "#181818",
  },
  classes: SHARED_CLASSES,
};

export function portalElementsAppearance() {
  return currentTheme() === "light" ? PORTAL_ELEMENTS_APPEARANCE_LIGHT : PORTAL_ELEMENTS_APPEARANCE_DARK;
}
